import { AgoraMetricsProcessor } from '../metrics/AgoraMetricsProcessor';
import { StudioLogger } from '@studio/logger/StudioLogger';
import { CAMERA_PRESENTER, AUDIO_PRESENTER } from '@studio/constants/Sources';
import type { Scene, ProgramLayer, Meeting, OnstageAttendees } from './interfaces';

const LOG = new StudioLogger('agoraOnstageAttendeeUpdater');

/**
 * Custom deep equality comparison function
 * Compares two values recursively to determine if they are equal
 */
function isEqual(a: any, b: any): boolean {
  // Strict equality check first
  if (a === b) return true;

  // Check for null/undefined
  if (a == null || b == null) return a === b;

  // Check if both are objects
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  // Check if both are arrays
  if (Array.isArray(a) !== Array.isArray(b)) return false;

  // Get object keys
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  // Check if they have the same number of keys
  if (keysA.length !== keysB.length) return false;

  // Recursively check each key
  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!isEqual(a[key], b[key])) return false;
  }

  return true;
}

let lastProgram: any = null;

/**
 * This function updates Agora metrics
 * processor with current onstage attendees information.
 */
export default function agoraOnstageAttendeeUpdater(scene: Scene, meeting: Meeting): void {
  try {
    if (!scene?.layers || !meeting?.client || !meeting?.attendeeId || !meeting?.started) {
      return;
    }

    if (lastProgram && isEqual(lastProgram, scene)) {
      return;
    }
    // Deep clone and cache the scene
    lastProgram = JSON.parse(JSON.stringify(scene));

    // Extract audio presenters
    const audioPresenters = scene.layers
      .filter((layer: ProgramLayer) => layer.source?.type === AUDIO_PRESENTER)
      .map((layer: ProgramLayer) => `user-${layer.source?.user}`)
      .filter(Boolean);

    // Build onstage attendees map
    const onstageAttendees: OnstageAttendees = {};
    for (const layer of scene.layers) {
      if (layer.source?.type === CAMERA_PRESENTER && layer.source?.user) {
        const attendeeId = `user-${layer.source?.user}`;
        if (attendeeId) {
          onstageAttendees[attendeeId] = {
            hasAudio: audioPresenters.includes(attendeeId)
          };
        }
      }
    }

    LOG.info('onstage attendees to update:', onstageAttendees);

    try {
      const processorInstance = AgoraMetricsProcessor.getInstance(meeting.client, meeting.attendeeId);
      processorInstance.updateOnstageAttendees(onstageAttendees);
    } catch (processorError) {
      LOG.error('Error creating or updating AgoraMetricsProcessor:', processorError);
    }
  } catch (error) {
    LOG.error('Error in agoraOnstageAttendeeUpdater:', error);
  }
}
